/**
 * Utilitário para gerenciar chaves de API a partir de variáveis de ambiente.
 * Mantém fallback para localStorage apenas durante o período de transição.
 */

// Lista de valores de placeholder comuns que devem ser ignorados
const PLACEHOLDER_VALUES = [
  'sua_chave_api_google_maps_aqui',
  'your_google_maps_api_key',
  'your_api_key_here',
  'sua_chave_api_openai_aqui',
  'seu_client_id_amadeus_aqui',
  'seu_client_secret_amadeus_aqui',
  'sua_chave_api_unsplash_aqui'
];

/**
 * Verifica se um valor é um placeholder que deve ser ignorado
 */
function isPlaceholder(value: string | undefined | null): boolean {
  if (!value) return true;
  return PLACEHOLDER_VALUES.includes(value.trim());
}

/**
 * Obtém a chave da API do Google Maps
 * Prioridade: Variável de ambiente, com fallback temporário para localStorage
 */
export function getGoogleMapsApiKey(): string | null {
  // Obter da variável de ambiente
  const envApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
  if (envApiKey && !isPlaceholder(envApiKey)) {
    return envApiKey;
  }
  
  // FALLBACK TEMPORÁRIO: Mostrar aviso no console se usar localStorage
  const localStorageKey = localStorage.getItem('googleApiKey');
  if (localStorageKey) {
    console.warn('Usando chave do Google Maps do localStorage. Esta funcionalidade será removida em breve. Por favor, configure no arquivo .env.');
    return localStorageKey;
  }
  
  return null;
}

/**
 * Obtém a chave da API da OpenAI
 * Prioridade: Variável de ambiente, com fallback temporário para localStorage
 */
export function getOpenAiApiKey(): string | null {
  // Obter da variável de ambiente
  const envApiKey = import.meta.env.VITE_OPENAI_API_KEY;
  if (envApiKey && !isPlaceholder(envApiKey)) {
    return envApiKey;
  }
  
  // FALLBACK TEMPORÁRIO: Mostrar aviso no console se usar localStorage
  const localStorageKey = localStorage.getItem('openaiApiKey');
  if (localStorageKey) {
    console.warn('Usando chave da OpenAI do localStorage. Esta funcionalidade será removida em breve. Por favor, configure no arquivo .env.');
    return localStorageKey;
  }
  
  return null;
}

/**
 * Obtém o Client ID da API da Amadeus
 * Prioridade: Variável de ambiente, com fallback temporário para localStorage
 */
export function getAmadeusClientId(): string | null {
  // Obter da variável de ambiente
  const envClientId = import.meta.env.VITE_AMADEUS_PROD_CLIENT_ID;
  if (envClientId && !isPlaceholder(envClientId)) {
    return envClientId;
  }
  
  // FALLBACK TEMPORÁRIO: Mostrar aviso no console se usar localStorage
  const localStorageClientId = localStorage.getItem('amadeusClientId');
  if (localStorageClientId) {
    console.warn('Usando Client ID da Amadeus do localStorage. Esta funcionalidade será removida em breve. Por favor, configure no arquivo .env.');
    return localStorageClientId;
  }
  
  return null;
}

/**
 * Obtém o Client Secret da API da Amadeus
 * Prioridade: Variável de ambiente, com fallback temporário para localStorage
 */
export function getAmadeusClientSecret(): string | null {
  // Obter da variável de ambiente
  const envClientSecret = import.meta.env.VITE_AMADEUS_PROD_CLIENT_SECRET;
  if (envClientSecret && !isPlaceholder(envClientSecret)) {
    return envClientSecret;
  }
  
  // FALLBACK TEMPORÁRIO: Mostrar aviso no console se usar localStorage
  const localStorageClientSecret = localStorage.getItem('amadeusClientSecret');
  if (localStorageClientSecret) {
    console.warn('Usando Client Secret da Amadeus do localStorage. Esta funcionalidade será removida em breve. Por favor, configure no arquivo .env.');
    return localStorageClientSecret;
  }
  
  return null;
}

/**
 * Obtém a chave da API do Unsplash
 * Prioridade: Variável de ambiente, com fallback temporário para localStorage
 */
export function getUnsplashApiKey(): string | null {
  // Obter da variável de ambiente
  const envApiKey = import.meta.env.VITE_UNSPLASH_API_KEY;
  if (envApiKey && !isPlaceholder(envApiKey)) {
    return envApiKey;
  }
  
  // FALLBACK TEMPORÁRIO: Mostrar aviso no console se usar localStorage
  const localStorageKey = localStorage.getItem('unsplashApiKey');
  if (localStorageKey) {
    console.warn('Usando chave do Unsplash do localStorage. Esta funcionalidade será removida em breve. Por favor, configure no arquivo .env.');
    return localStorageKey;
  }
  
  return null;
}

/**
 * Obtém a chave da API do Google para Custom Search (imagens)
 * Usa a mesma chave que o Google Maps, com a mesma prioridade
 */
export function getGoogleCustomSearchApiKey(): string | null {
  // Reutiliza a mesma chave do Google Maps
  return getGoogleMapsApiKey();
} 